import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import {
  useDeleteSubscriptionPackage,
  useUpdateSubscriptionPackage,
} from "@/hooks/use-mutations";
import {
  useGetAdminBins,
  useGetSubscriptionPackages,
  useGetUser,
} from "@/hooks/use-queries";
import { cardStyleFor } from "@/lib/card-style";
import { formatAmount } from "@/lib/utils";

type Bin = {
  id: number;
  bin: string;
  provider: string;
  network?: string;
  isActive?: boolean;
};

const packageNames: Record<string, string> = {
  "1": "Monthly",
  "12": "Yearly",
};

const isPlatinumProvider = (provider: string) =>
  provider === "wsb" || provider === "ptp";

const durationOptions = [
  { value: "1", label: "1 Month" },
  { value: "12", label: "12 Months" },
];

const PackageRow = ({
  pkg,
  bin,
  whiteLabelId,
  takenDurations,
}: {
  pkg: SubscriptionPackage;
  bin: Bin;
  whiteLabelId: string;
  takenDurations: number[];
}) => {
  const queryClient = useQueryClient();
  const isPlatinum = isPlatinumProvider(bin.provider);
  const hasCustomPrice = bin.provider !== "int";
  const [isEditing, setIsEditing] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [durationMonths, setDurationMonths] = useState(
    String(pkg.durationMonths),
  );
  const [price, setPrice] = useState(String(parseFloat(pkg.price)));
  const [defaultPrice, setDefaultPrice] = useState(
    String(parseFloat(pkg.defaultPrice) || ""),
  );

  const name = packageNames[durationMonths];

  const cancelEdit = () => {
    setDurationMonths(String(pkg.durationMonths));
    setPrice(String(parseFloat(pkg.price)));
    setDefaultPrice(String(parseFloat(pkg.defaultPrice) || ""));
    setIsEditing(false);
  };

  const refresh = () =>
    queryClient.invalidateQueries({
      queryKey: ["subscriptionPackages", whiteLabelId],
    });

  const { mutate: updatePackage, isPending: isUpdating } =
    useUpdateSubscriptionPackage({
      whiteLabelId,
      onSuccess: () => {
        refresh();
        setIsEditing(false);
      },
    });
  const { mutate: deletePackage, isPending: isDeleting } =
    useDeleteSubscriptionPackage({ whiteLabelId, onSuccess: refresh });

  const handleSave = () =>
    updatePackage({
      packageId: pkg.id,
      data: {
        name,
        durationMonths: Number(durationMonths) as 1 | 12,
        price: Number(price),
        ...(isPlatinum && { defaultPrice: Number(defaultPrice) }),
      },
    });

  const priceField = (
    label: string,
    value: string,
    onChange?: (v: string) => void,
  ) => (
    <div className="flex flex-col gap-1.5">
      <label className="text-[10px] font-normal">{label}</label>
      <Input
        type="number"
        step="0.01"
        className="rounded"
        value={value}
        disabled={!onChange}
        readOnly={!onChange}
        onChange={(e) => onChange?.(e.target.value)}
      />
    </div>
  );

  return (
    <div
      className="rounded-md border border-[#DAE1EA] p-2.5 text-xs"
      style={{ boxShadow: "0px 1px 1px 0px #0000000D" }}
    >
      {isEditing ? (
        <>
          <div className="grid grid-cols-2 gap-2.5">
            <div className="flex flex-col gap-1.5">
              <label className="text-[10px] font-normal">Package Name</label>
              <Input className="rounded" value={name} disabled readOnly />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-[10px] font-normal">Duration</label>
              <Select value={durationMonths} onValueChange={setDurationMonths}>
                <SelectTrigger className="w-full rounded">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {durationOptions
                    .filter(
                      (option) =>
                        !takenDurations.includes(Number(option.value)),
                    )
                    .map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-[10px] font-normal">Dgw Fee (%)</label>
              <Input
                className="rounded"
                value={pkg.platformFeePercent}
                disabled
                readOnly
              />
            </div>
            {hasCustomPrice &&
              priceField(
                "Base Custom Price ($)",
                String(parseFloat(pkg.basePrice)),
              )}
            {priceField(
              hasCustomPrice ? "Custom Price ($)" : "Price ($)",
              price,
              setPrice,
            )}
            {isPlatinum && (
              <>
                {priceField(
                  "Base Default Price ($)",
                  String(parseFloat(pkg.baseDefaultPrice) || ""),
                )}
                {priceField("Default Price ($)", defaultPrice, setDefaultPrice)}
              </>
            )}
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={isUpdating}
              className="w-20 rounded-md text-xs"
              onClick={cancelEdit}
            >
              Cancel
            </Button>
            <Button
              type="button"
              isLoading={isUpdating}
              disabled={isUpdating || !price || (isPlatinum && !defaultPrice)}
              className="bg-primary-500 hover:bg-primary-500/90 w-20 rounded-md text-xs"
              onClick={handleSave}
            >
              Save
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <span className="font-medium">{pkg.name}</span>
              <div className="mt-1.5 flex flex-col gap-0.5 text-[#A5ACB6]">
                <span>
                  {hasCustomPrice ? "Custom Price" : "Price"}: $
                  {formatAmount(parseFloat(pkg.price))}
                  {hasCustomPrice &&
                    pkg.defaultPrice &&
                    ` / Default: $${formatAmount(parseFloat(pkg.defaultPrice))}`}
                </span>
                <span>Dgw Fee: {pkg.platformFeePercent}%</span>
              </div>
            </div>
            <div className="flex shrink-0 flex-col items-stretch gap-1.5">
              {isConfirmingDelete ? (
                <>
                  <span className="text-center text-[10px] text-[#FF6366]">
                    Delete this package?
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    isLoading={isDeleting}
                    disabled={isDeleting}
                    className="rounded-md text-xs"
                    onClick={() => deletePackage(pkg.id)}
                  >
                    Yes, delete
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={isDeleting}
                    className="rounded-md text-xs"
                    onClick={() => setIsConfirmingDelete(false)}
                  >
                    No
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    type="button"
                    size="sm"
                    className="bg-primary-500 hover:bg-primary-500/90 rounded-md text-xs"
                    onClick={() => setIsEditing(true)}
                  >
                    Edit
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    className="rounded-md text-xs"
                    onClick={() => setIsConfirmingDelete(true)}
                  >
                    Delete
                  </Button>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const PackageList = ({
  packages,
  isLoading,
  bin,
  whiteLabelId,
}: {
  packages: SubscriptionPackage[];
  isLoading: boolean;
  bin: Bin;
  whiteLabelId: string;
}) => {
  if (isLoading) {
    return <p className="mt-2 text-xs text-[#A5ACB6]">Loading packages...</p>;
  }
  if (packages.length === 0) {
    return <p className="mt-2 text-xs text-[#A5ACB6]">No packages found.</p>;
  }
  return (
    <div className="mt-2 space-y-2">
      {packages.map((pkg) => (
        <PackageRow
          key={pkg.id}
          pkg={pkg}
          bin={bin}
          whiteLabelId={whiteLabelId}
          takenDurations={packages
            .filter((other) => other.id !== pkg.id)
            .map((other) => other.durationMonths)}
        />
      ))}
    </div>
  );
};

const BinPackages = ({
  bin,
  whiteLabelId,
  packages,
  isLoading,
}: {
  bin: Bin;
  whiteLabelId: string;
  packages: SubscriptionPackage[];
  isLoading: boolean;
}) => (
  <>
    <div className="mt-4 rounded border p-2.5">
      <h5 className="text-[#0A2259FA]">Subscription Packages</h5>
      <PackageList
        packages={packages}
        isLoading={isLoading}
        bin={bin}
        whiteLabelId={whiteLabelId}
      />
    </div>
    <p className="mt-4 text-xs text-[#A5ACB6]">
      To add subscription packages for this card, please contact Digitwallet.
      They will create the packages so you can update the prices here.
    </p>
  </>
);

const SubscriptionSettings = () => {
  const { data: user } = useGetUser();
  const whiteLabelId = user?.whiteLabelId || "";
  const { data: allBins, isLoading: isBinsLoading } =
    useGetAdminBins(whiteLabelId);
  const { data: allPackages, isLoading: isPackagesLoading } =
    useGetSubscriptionPackages(whiteLabelId);

  const bins = (allBins ?? []).filter((bin: Bin) => bin.isActive !== false);

  return (
    <>
      <div className="text-primary-500 mt-4 bg-[#DFEEFF] px-4 py-4">
        <h2 className="text-xl font-bold">Subscription Settings</h2>
      </div>

      <div className="mt-4 px-4">
        {!whiteLabelId || isBinsLoading ? (
          <p className="text-xs text-[#A5ACB6]">Loading bins...</p>
        ) : bins.length === 0 ? (
          <p className="text-xs text-[#A5ACB6]">No bins found.</p>
        ) : (
          <Tabs defaultValue={String(bins[0].id)}>
            <TabsList className="no-scrollbar *:data-[state=active]:text-primary-500 flex w-full justify-start overflow-x-auto rounded border border-[#3D69C580] bg-[#F9F9F9] p-0 *:shrink-0 *:rounded *:text-[10px] *:text-[#07173D66] *:data-[state=active]:bg-[#DFEEFF]">
              {bins.map((bin: Bin) => (
                <TabsTrigger key={bin.id} value={String(bin.id)}>
                  {`${cardStyleFor(bin.provider, bin.network).tierLabel} (${bin.bin})`}
                </TabsTrigger>
              ))}
            </TabsList>
            {bins.map((bin: Bin) => (
              <TabsContent key={bin.id} value={String(bin.id)}>
                <BinPackages
                  bin={bin}
                  whiteLabelId={whiteLabelId}
                  isLoading={isPackagesLoading}
                  packages={(allPackages ?? []).filter(
                    (pkg: SubscriptionPackage) => pkg.binId === bin.id,
                  )}
                />
              </TabsContent>
            ))}
          </Tabs>
        )}
      </div>
    </>
  );
};

export default SubscriptionSettings;
